import { createRoute, Link } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { AppHeader } from '@/components/AppHeader'
import { usePlatform } from '@/hooks/usePlatform'
import {
  ROADMAP,
  LANE_LABELS,
  LANE_DESCRIPTIONS,
  type RoadmapLane,
  type RoadmapItem,
} from '@/data/roadmap'

export const roadmapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/roadmap',
  component: RoadmapPage,
})

const LANE_ORDER: RoadmapLane[] = ['shipped', 'building', 'next', 'later']

const LANE_ACCENT: Record<RoadmapLane, string> = {
  shipped: 'border-success/40 bg-success/5 text-success',
  building: 'border-accent/40 bg-accent-muted text-accent',
  next: 'border-second/40 bg-[color:var(--second-soft)] text-second',
  later: 'border-brd-subtle bg-surface-sunken text-content-secondary',
}

function RoadmapPage() {
  const platform = usePlatform()
  // On native the landing page is unreachable, so "Back to home"
  // routes into the authenticated shell instead of the marketing
  // root. Web users continue to land on `/` (the public landing).
  const homeHref = platform.isNative ? '/home' : '/'
  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-surface-base to-surface-raised text-content-primary">
      <AppHeader />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-6 sm:pb-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
          Roadmap
        </h1>
        <p className="text-sm sm:text-base text-content-secondary max-w-2xl leading-relaxed">
          Two top-level apps today — <strong>Bussboard</strong> (node-graph
          signal-flow canvas) and <strong>Werkshop</strong> (the DAW: Sequencer,
          MIDI Router, Sampler, Piano Roll, Automation, Store) — plus
          multiplayer scaffolding. This page is the single source of truth for
          what we&apos;re building next and what founding-member subscribers can
          expect during their price-locked subscription window.
        </p>
        <div className="mt-4 inline-flex items-start gap-2 rounded-lg border border-accent/30 bg-accent-muted px-4 py-3 text-[12px] text-content-primary">
          <span className="text-accent font-semibold uppercase tracking-wider text-[10px] mt-0.5">
            Founding member lock
          </span>
          <span className="text-content-secondary">
            Every paid subscription started before new apps launch keeps its
            current bundle price for the lifetime of that subscription.
          </span>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 flex flex-col gap-10">
        {LANE_ORDER.map(lane => (
          <LaneBlock key={lane} lane={lane} items={ROADMAP[lane]} />
        ))}
      </section>

      <footer className="border-t border-brd-subtle py-8 text-center text-xs text-content-tertiary px-4">
        <p>
          Dates on future items are intent, not commitments. We&apos;ll update
          this page as things land.{' '}
          <Link
            to={homeHref}
            className="underline underline-offset-4 hover:text-content-primary"
          >
            Back to home →
          </Link>
        </p>
      </footer>
    </div>
  )
}

function LaneBlock({
  lane,
  items,
}: {
  lane: RoadmapLane
  items: RoadmapItem[]
}) {
  return (
    <div>
      <div className="flex items-baseline gap-3 mb-4">
        <h2 className="text-xl sm:text-2xl font-bold">{LANE_LABELS[lane]}</h2>
        <span className="text-[11px] font-mono uppercase tracking-wider text-content-tertiary">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>
      <p className="text-xs text-content-tertiary mb-4">
        {LANE_DESCRIPTIONS[lane]}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map(item => (
          <RoadmapCard key={item.title} item={item} lane={lane} />
        ))}
      </div>
    </div>
  )
}

function RoadmapCard({ item, lane }: { item: RoadmapItem; lane: RoadmapLane }) {
  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-2 ${LANE_ACCENT[lane]}`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-content-primary">
          {item.title}
        </h3>
        {item.app && (
          <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-raised border border-brd-subtle text-content-tertiary">
            {item.app}
          </span>
        )}
      </div>
      <p className="text-xs text-content-secondary leading-relaxed">
        {item.description}
      </p>
      {item.when && (
        <p className="text-[10px] font-mono uppercase tracking-wider text-content-tertiary mt-auto pt-1">
          {item.when}
        </p>
      )}
    </div>
  )
}
